/**
 * GIF Button Component
 * Circular button that opens the GIF picker modal
 */

import { useState, lazy, Suspense } from 'react'
import type { GifAttachment } from './types'

// Lazy load the GIF picker for performance
const GifPicker = lazy(() => import('./GifPicker').then(m => ({ default: m.GifPicker })))

interface GifButtonProps {
  onGifSelect: (gif: GifAttachment) => void
  disabled?: boolean
  userId: string
}

export function GifButton({ onGifSelect, disabled = false, userId }: GifButtonProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false)

  const handleClick = () => {
    if (!disabled) {
      setIsPickerOpen(true)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className={`p-3 rounded-full transition-all duration-200 ${
          disabled
            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
            : 'bg-sky-50 text-sky-600 hover:bg-sky-100 border border-sky-200'
        }`}
        title="Send GIF"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.5 9H13v6h-1.5zM9 9H6c-.6 0-1 .5-1 1v4c0 .5.4 1 1 1h3c.6 0 1-.5 1-1v-2H8.5v1.5h-2v-3H10V10c0-.5-.4-1-1-1zm10 1.5V9h-4.5v6H16v-2h2v-1.5h-2v-1z"/>
        </svg>
      </button>

      {/* Lazy loaded GIF picker */}
      {isPickerOpen && (
        <Suspense fallback={<PickerSkeleton />}>
          <GifPicker
            isOpen={isPickerOpen}
            onClose={() => setIsPickerOpen(false)}
            onSelect={onGifSelect}
            userId={userId}
          />
        </Suspense>
      )}
    </>
  )
}

// Loading skeleton for GIF picker
function PickerSkeleton() {
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-white w-full md:max-w-lg md:rounded-xl rounded-t-2xl max-h-[90vh] md:max-h-[80vh] p-4 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="h-12 bg-gray-200 rounded mb-4" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="aspect-square bg-gray-200 rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  )
}
